import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMarketConfig } from '~/app/market-config';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import useConflictSolutionGroups from '~/app/tracking/use-tracking-solution-groups';
import {
	AcceptConflictSolutionQuery,
	CheckConflictsQuery,
	ConfiguredCarFeature,
} from '~/graphql/__generated__/graphql';
import {
	imageSetFilter,
	useAcceptConflictSolutions,
	useChangeConflictSolutions,
} from '~/graphql/hooks/use-configured-car-queries';
import { removeTypenameFields } from '~/graphql/utils';
import { expect } from '~/utils/unwrap';

export type ConflictData = NonNullable<CheckConflictsQuery['change']['conflict']>;
export type ConflictSolution = NonNullable<ConflictData>['solutionGroups'][0]['solutions'][0];
export type ConflictItem = ConflictSolution['feature'];

type IndexType = `${number}:${number}`;

export type AcceptedSolutionData =
	| {
			status: 'success';
			data: AcceptConflictSolutionQuery['acceptConflictSolutionsByUrl'];
	  }
	| {
			status: 'error';
			error: unknown;
	  }
	| {
			status: 'loading';
	  }
	| {
			status: 'idle';
	  };

export function useConflictHandler(initialConflictData: ConflictData) {
	const { config, formatter } = useMarketConfig();
	const { changeConflictSolutions, isChangingConflictSolutions } = useChangeConflictSolutions();
	const { acceptConflictSolutions, isAcceptingConflictSolutions } = useAcceptConflictSolutions();
	const [conflictData, setConflictData] = useState(initialConflictData);

	const [solutions, setSolutions] = useState<string[]>([]);
	const solutionRef = useRef<string[]>([]);
	const [acceptedSolutionData, setAcceptedSolutionData] = useState<AcceptedSolutionData>({
		status: 'idle',
	});
	const lastLoadingIndexRef = useRef<IndexType | undefined>(undefined);
	const [loadingIndex, setLoadingIndex] = useState<IndexType | undefined>(undefined);
	const trackingSolutionGroups = useConflictSolutionGroups(initialConflictData);

	const isItemLoading = useCallback(
		(groupIndex: number, index: number) => {
			return loadingIndex === `${groupIndex}:${index}`;
		},
		[loadingIndex],
	);

	const sign = useMemo(
		() =>
			conflictData && conflictData.price
				? conflictData.price.value === 0
					? ''
					: conflictData.price.value > 0
						? '+ '
						: '- '
				: '',
		[conflictData.price],
	);

	// Using absolute value, because price value can be negative
	// We always want to display the sign (+ or -), which is not default behavior of our formatter
	const priceChange = useMemo(
		() =>
			conflictData && conflictData.price
				? `${sign}${formatter.formatPrice(Math.abs(conflictData?.price.value))}`
				: '',
		[sign, formatter, conflictData?.price?.value],
	);

	const totalPrice = useMemo(() => {
		return acceptedSolutionData.status === 'success'
			? acceptedSolutionData.data?.configuredCar.prices?.priceParts.find(
					(price) => price.type === config.uiPreferences.prices.totalPriceFrom,
				)
			: null;
	}, [acceptedSolutionData]);

	useEffect(() => {
		if (!isChangingConflictSolutions && loadingIndex !== undefined) {
			setLoadingIndex(undefined);
		} else if (isChangingConflictSolutions && !!lastLoadingIndexRef.current) {
			setLoadingIndex(lastLoadingIndexRef.current);
		}
	}, [isAcceptingConflictSolutions, isChangingConflictSolutions, loadingIndex]);

	const trigger = expect(conflictData?.trigger, 'Expected conflict trigger');
	const solutionGroups = expect(conflictData?.solutionGroups, 'Expected solution groups');
	const decisionRequired = solutionGroups.filter((g) => g.solutions.length > 1);

	const automaticallyAdded = solutionGroups
		.filter((g) => g.solutions.length === 1 && g.solutions[0]?.action === 'ADD')
		.flatMap((g) => g.solutions)
		.map((s) => s.feature);

	const automaticallyRemoved = solutionGroups
		.filter((g) => g.solutions.length === 1 && g.solutions[0]?.action === 'REMOVE')
		.flatMap((g) => g.solutions)
		.map((s) => s.feature);

	async function acceptConflictSolution() {
		if (conflictData?.acceptUrl) {
			const result = await acceptConflictSolutions({
				variables: {
					financingInput: config.financingInput,
					input: {
						acceptUrl: conflictData?.acceptUrl,
						brand: conflictData?.id?.brand,
						country: conflictData?.id?.country,
						language: conflictData?.id?.language,
					},
				},
			});

			if (result.data) {
				setAcceptedSolutionData({
					status: 'success',
					data: expect(
						result.data.acceptConflictSolutionsByUrl,
						'Expected configuredCar from acceptConflictSolutions',
					),
				});
			} else if (result.error) {
				setAcceptedSolutionData({
					status: 'error',
					error: result.error,
				});
			}
		}
	}

	useEffect(() => {
		// Calculates the price for the default solution and checks, whether it's buildable
		// const initialSolutions = solutions?.length ? solutions : getSelectedItems(solutionGroups);
		acceptConflictSolution();
	}, [solutions.length]);

	async function changeSelection(item: ConflictItem, layerIndex: number, itemIndex: number) {
		if (!item.status.changeable) {
			console.warn(
				'The selected item is not changeable. Try replacing it with an equivalent item instead.',
				item,
			);
			return;
		}

		setLoadingIndex(`${layerIndex}:${itemIndex}`);
		lastLoadingIndexRef.current = `${layerIndex}:${itemIndex}`;

		const decisionsPerLayer = decisionRequired
			.map((group) => group.solutions.find((s) => s.feature.status.selected))
			.map((s) => expect(s?.feature, 'Expected feature'));

		decisionsPerLayer[layerIndex] = item;

		const selectedSolutions = [
			...decisionsPerLayer.map((pr7) => expect(pr7, 'Expected pr7')),
			...automaticallyAdded,
		];

		setSolutions(selectedSolutions.map((selected) => selected.pr7));
		solutionRef.current = selectedSolutions.map((selected) => selected.pr7);
		const changedSolution = await changeConflictSolutions({
			variables: {
				financingInput: config.financingInput,
				financingEnabled: config.enablePartsRateCalculation && config?.enableFinancingServices,
				input: {
					id: removeTypenameFields(conflictData.id),
					conflictTriggerId: trigger.pr7,
					lastGoodConfiguredCarIdentifier: removeTypenameFields(conflictData.lastGoodConfiguredCarIdentifier),
					solutions: getSelectedItems(conflictData.solutionGroups),
					set: item.pr7,
				},
				imageTypeFilterInput: imageSetFilter,
			},
		});

		setConflictData(expect(changedSolution.data?.changeConflictSolutions.conflict));

		if (changedSolution.data) {
			setAcceptedSolutionData({
				status: 'loading',
			});

			if (changedSolution.data?.changeConflictSolutions?.conflict?.acceptUrl) {
				const acceptedSolution = await acceptConflictSolutions({
					variables: {
						financingInput: config.financingInput,
						input: {
							brand: conflictData?.id?.brand,
							country: conflictData?.id?.country,
							language: conflictData?.id?.language,
							acceptUrl: changedSolution.data?.changeConflictSolutions?.conflict?.acceptUrl,
						},
					},
				});

				if (acceptedSolution.data) {
					setAcceptedSolutionData({
						status: 'success',
						data: acceptedSolution.data.acceptConflictSolutionsByUrl,
					});
					appTrackingService.trackConflictItemSelectionEvent({
						selectedItem: item as ConfiguredCarFeature,
						priceChange: changedSolution?.data.changeConflictSolutions.conflict.price?.value || 0,
						newTotalPrice: `${
							acceptedSolution.data?.acceptConflictSolutionsByUrl?.configuredCar?.prices?.priceParts.find(
								(price) => price.type === config.uiPreferences.prices.totalPriceFrom,
							)?.price.value || ''
						}`,
						label: item.name || '',
						elementName: 'radio button',
						elementValue: trackingSolutionGroups[0]?.name,
						eventName: 'configuration assistant - select configuration item',
						clickId: 'selection via configuration assistant',
					});
				} else if (acceptedSolution.error) {
					setAcceptedSolutionData({
						status: 'error',
						error: acceptedSolution.error,
					});
				}
			}
		}
	}

	return {
		trigger,
		sign,
		priceChange,
		totalPrice,
		decisionRequired,
		automaticallyAdded,
		automaticallyRemoved,
		acceptedSolutionData,
		isLoading: isChangingConflictSolutions || isAcceptingConflictSolutions,
		isChangingConflictSolutions,
		isItemLoading,
		changeSelection,
		updatedConflictData: conflictData,
		isItemSelected: (item: ConflictItem) => item.status.selected,
		trackingSolutionGroups,
	};
}

function getSelectedItems(groups: NonNullable<ConflictData>['solutionGroups']) {
	return groups
		.flatMap((g) => g.solutions)
		.filter((s) => s.feature.status.selected)
		.map((s) => s.feature.pr7);
}
